import { ContentState } from 'draft-js';
import { CellData } from '../../../../../../common/table/TableTypes';
import {
    TABLE_DEFAULT_COLORS,
    TABLE_DEFAULT_HEADER_BACKGROUND_COLOR,
} from '../../../../../../common/table/tableConstants';
import { getColorDisplayValue } from '../../../../../../common/colors/elementColorFormatUtil';
import { getBackgroundColorClasses } from '../../../../utils/elementColorStyleUtils';
import getContentInlineStyle from '../../../../../components/editor/customRichUtils/contentState/getContentInlineStyle';
import { STYLE_COMMANDS } from '../../../../../components/editor/richText/richTextConstants';

/**
 * Returns the rendered background color for a cell.
 */
export const getCellBackgroundColor = (cellData?: CellData): string => {
    const { background = null } = cellData || {};
    const colorValue = getColorDisplayValue(background);

    if (colorValue && colorValue.match(/^#([0-9a-f]{3}){1,2}$/i)) return colorValue;
    if (background === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR) return 'var(--ws-table-header-row-background)';

    return 'var(--ws-element-background-primary)';
};

/**
 * Returns the background class flags for a cell renderer.
 */
export const getBackgroundColorClass = (cellData?: CellData): Record<string, boolean> | undefined => {
    const { background = null } = cellData || {};
    if (!background) return;
    if (background === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR) return { 'colored-background': true };

    return getBackgroundColorClasses(background);
};

/**
 * Resolves a table background token to a concrete hex color.
 */
export const getBackgroundColorHex = (color: string | null | undefined, isDarkMode: boolean): string => {
    if (!color) return isDarkMode ? '#333333' : '#ffffff';
    if (color === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR) return isDarkMode ? '#4D4D4D' : '#D2D3D6';

    // @ts-ignore - if it's not a valid key then we just return the color value
    return TABLE_DEFAULT_COLORS[color]?.hex || color;
};

/**
 * Returns the cell-level styles that apply uniformly across the full content.
 */
export const getCellLevelTextStyles = (content: ContentState): string[] => {
    return Array.from(getContentInlineStyle(content) || []).filter(
        (style) =>
            ![
                STYLE_COMMANDS.LARGE_HEADING,
                STYLE_COMMANDS.HEADING,
                STYLE_COMMANDS.SMALL_TEXT,
                STYLE_COMMANDS.CODE,
                STYLE_COMMANDS.BLOCKQUOTE,
            ].includes(style),
    );
};
